import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Address3HelperService, AddressFormGroup } from '@shared/components/address3/address3-helper.service';
import { CustomValidators } from '@shared/customValidators';
import { SELECT_INVALID_SOLDO_FIELD, scrollToFirstResult } from '@shared/formHelpers';
import { CountryMetadata, IDVSessionDetails, Town, UserIDVDetails } from '@shared/models/data-model';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { CountryService, MAIN_MARKETS_COUNTRY_ISO } from '@shared/services/country.service';
import { FiscalCodeService } from '@shared/services/fiscal-code.service';
import { IdvService } from '@shared/services/idv.service';
import { LoadingScreenService } from '@shared/services/loading-screen.service';
import { RegistrationService } from '@shared/services/registration.service';
import moment from 'moment-mini';
import { NGXLogger } from 'ngx-logger';
import { distinctUntilChanged } from 'rxjs/operators';

interface InternalUserDetailsFields {
    firstName: FormControl<string>;
    middleName: FormControl<string>;
    lastName: FormControl<string>;
    nationality: FormControl<string | null>;
    address: FormGroup<AddressFormGroup>;
    dateOfBirth: FormControl<string | null>;
    codiceFiscaleChoice?: FormControl<boolean | null>;
    codiceFiscale?: FormControl<string | null>;
}

@Component({
    selector: 'internal-user-idv',
    templateUrl: './internal-user-idv.component.html',
    styleUrl: './internal-user-idv.component.scss'
})
export class InternalUserIdvComponent implements OnInit {
    loading: boolean = true;
    completeCountryList: CountryMetadata[];
    translatedCountries: CountryMetadata[] = [];
    tokenVerified: boolean = false;
    sessionData: IDVSessionDetails = null;
    idvUserForm: FormGroup<InternalUserDetailsFields> = null;

    constructor(
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly fb: FormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly idvService: IdvService,
        private readonly countryService: CountryService,
        private readonly fiscalCodeService: FiscalCodeService,
        private readonly amplitudeService: AmplitudeService,
        private readonly addressHelperService: Address3HelperService,
        private readonly destroyRef: DestroyRef,
        private readonly translate: TranslateService,
        private readonly loadingScreenService: LoadingScreenService
    ) { }

    ngOnInit(): void {
        this.loadingScreenService.show('LOADING_SCREEN.COUNTRY_TRANSLATION');
        this.initializeCountryList().then(() => {
            this.loadingScreenService.hide();
            this.translate.onLangChange.pipe(
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(async () => {
                this.loadingScreenService.show('LOADING_SCREEN.COUNTRY_TRANSLATION');

                this.initializeCountryList().then(() => {
                    this.loadingScreenService.hide();
                });
            });
        });

        const idvData = this.idvService.getCurrentIdvSessionData();
        this.logger.debug("idvData", idvData);

        this.registrationService.getIdvSession(idvData.personExternalId).then((sessionDetails) => {
            this.sessionData = sessionDetails;

            if (this.sessionData.workflowRunId) {
                idvData.idvReference = this.sessionData.workflowRunId;
                this.idvService.setCurrentIdvSessionData(idvData);
                this.proceedToIDV();
                this.amplitudeService.trackInternalUserIdvResumed();
            } else {
                this.initDetailsForm();
                this.tokenVerified = true;
                this.loading = false;
                this.amplitudeService.trackInternalUserIdvStarted();
            }

        }, (err) => {
            if (err.status == 401) {
                this.tokenVerified = false;
                this.loading = false;
            }
            this.amplitudeService.trackInternalUserIdvError(err.status == 401);
        });
    }

    private async initializeCountryList(): Promise<void | CountryMetadata[]> {
        const allCountries = await this.countryService.getCurrentCountries();
        this.translatedCountries = allCountries;
        const preferredCountries = allCountries.filter(c => MAIN_MARKETS_COUNTRY_ISO.includes(c.iso3));
        preferredCountries.sort(this.countryService.sortFunction);
        const otherCountries = allCountries.filter(c_1 => c_1.icc && !MAIN_MARKETS_COUNTRY_ISO.includes(c_1.iso3));
        otherCountries.sort(this.countryService.sortFunction);
        this.completeCountryList = preferredCountries.concat(otherCountries);
    }

    initDetailsForm() {
        this.idvUserForm = this.fb.group({
            firstName: [this.sessionData?.firstName],
            middleName: [this.sessionData?.middleName],
            lastName: [this.sessionData?.lastName],
            dateOfBirth: [null as string, [Validators.required, CustomValidators.dateValidatorFactory(18, 120)]],
            nationality: [null as string, Validators.required],
            address: this.addressHelperService.createEmptyAddressFormGroup(null)
        });

        this.addCodiceFiscaleChoiceField();

        this.idvUserForm.controls.dateOfBirth.valueChanges.subscribe(() => {
            if (this.idvUserForm.controls.codiceFiscale) {
                this.idvUserForm.controls.codiceFiscale.updateValueAndValidity();
            }
        });

        this.idvUserForm.valueChanges.pipe(distinctUntilChanged()).subscribe((values) => {
            this.logger.debug("Form valueChanges", values);
            if ([values.nationality, values.address.countryISO].includes("ITA")) {
                this.removeCodiceFiscaleChoiceField();
                this.addCodiceFiscaleField();
            } else if (values.codiceFiscaleChoice) {
                this.addCodiceFiscaleField();
            } else {
                this.removeCodiceFiscale();
                this.addCodiceFiscaleChoiceField();
            }
        });
    }

    addCodiceFiscaleField() {
        if (!this.idvUserForm.get("codiceFiscale")) {
            this.logger.debug("adding codiceFiscale");
            this.idvUserForm.addControl("codiceFiscale", new FormControl<string | null>(null, [
                Validators.required,
                this.codiceFiscaletNameValidatorFactory(`${this.sessionData.firstName} ${this.sessionData.middleName ?? ''}`, this.sessionData.lastName),
                this.codiceFiscaleDateValidatorFactory(this.idvUserForm.controls.dateOfBirth)
            ], [this.fiscalCodeService.validator]), { emitEvent: false });
        }
    }

    removeCodiceFiscale() {
        if (this.idvUserForm.controls.codiceFiscale) {
            this.logger.debug("removing codiceFiscale");
            this.idvUserForm.removeControl("codiceFiscale", { emitEvent: false });
        }
    }

    addCodiceFiscaleChoiceField() {
        if (!this.idvUserForm.controls.codiceFiscaleChoice) {
            this.logger.debug("adding codiceFiscaleChoice");
            this.idvUserForm.addControl("codiceFiscaleChoice", new FormControl<boolean | null>(false, [Validators.required]), { emitEvent: false });
        }
    }

    removeCodiceFiscaleChoiceField() {
        if (this.idvUserForm.controls.codiceFiscaleChoice) {
            this.logger.debug("removing codiceFiscaleChoice");
            this.idvUserForm.removeControl("codiceFiscaleChoice", { emitEvent: false });
        }
    }

    codiceFiscaletNameValidatorFactory(firstName: string, lastName: string): ValidatorFn {
        return (codiceFiscaleControl: AbstractControl): ValidationErrors => {
            if (codiceFiscaleControl.value && codiceFiscaleControl.valid) {
                const checkAgainstFirstName = this.fiscalCodeService.validateAgainstFirstName(codiceFiscaleControl.value, firstName);
                const checkAgainstLastName = this.fiscalCodeService.validateAgainstLastName(codiceFiscaleControl.value, lastName);
                if (!checkAgainstFirstName || !checkAgainstLastName) {
                    return {
                        "nameCfMismatch": true
                    };
                }
            }
            return null;
        };
    }

    codiceFiscaleDateValidatorFactory(dateControl: FormControl<string>): ValidatorFn {
        return (codiceFiscaleControl: AbstractControl): ValidationErrors => {
            if (codiceFiscaleControl.value && codiceFiscaleControl.valid && dateControl.value && dateControl.valid) {
                const cfDate = moment(FiscalCodeService.retrieveBirthDate(codiceFiscaleControl.value)).format('DD/MM/YYYY');
                if (cfDate != dateControl.value) {
                    return {
                        "dateCfMismatch": true
                    };
                }
            }
            return null;
        };
    }

    async submitDetailsForm() {
        this.idvUserForm.markAllAsTouched();
        this.idvUserForm.updateValueAndValidity();
        this.logger.debug("The form is ", this.idvUserForm.valid ? "VALID" : "INVALID");
        if (this.idvUserForm.valid) {
            const formValue = this.idvUserForm.value;

            let codiceFiscaleBirthPlace: Town = null;
            let codiceFiscaleDateOfBirth: string = null;
            const codiceFiscale = formValue.codiceFiscale;
            if (codiceFiscale) {
                codiceFiscaleBirthPlace = await (this.fiscalCodeService.retrieveBirthPlace(codiceFiscale));
                codiceFiscaleDateOfBirth = moment(FiscalCodeService.retrieveBirthDate(codiceFiscale)).format('DD/MM/YYYY');
            }

            const idvData: UserIDVDetails = {
                nationality: formValue.nationality,
                fiscalCode: formValue.codiceFiscale,
                birthDate: moment(codiceFiscaleDateOfBirth ?? formValue.dateOfBirth, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                birthPlace: codiceFiscaleBirthPlace?.name ?? null,
                birthProvince: codiceFiscaleBirthPlace?.province ?? null,
                address: {
                    countryISO: formValue.address.countryISO,
                    line1: formValue.address.line1,
                    line2: formValue.address.line2,
                    province: formValue.address.province,
                    postalCode: formValue.address.postalCode,
                    searchRefCode: formValue.address.searchRefCode,
                    city: formValue.address.city
                }
            }
            this.loading = true;
            this.registrationService.postUserIdvData(this.sessionData.userKYCRequestId, idvData).then((workflowRunId) => {
                this.logger.debug("INT USER IDV RETURNS ", workflowRunId);
                if (workflowRunId) {
                    const idvData = this.idvService.getCurrentIdvSessionData();
                    this.idvService.setCurrentIdvSessionData({
                        ...idvData,
                        idvReference: workflowRunId
                    });
                    this.proceedToIDV();
                    this.amplitudeService.trackInternalUserIdvFormSubmitted();
                }
            });
        } else {
            scrollToFirstResult(SELECT_INVALID_SOLDO_FIELD);
        }
        return false;
    }

    proceedToIDV() {
        this.router.navigate(['static/verify-with-onfido']);
    }
}
